import { useEffect, useRef, useState } from "react";
import { RotateCcw, Send, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";

const linkify = (text) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p} target="_blank" rel="noreferrer" className="break-all text-[#53bdeb] underline">
        {p.length > 60 ? p.slice(0, 60) + "…" : p}
      </a>
    ) : (
      <BoldText key={i} text={p} />
    )
  );
};

const BoldText = ({ text }) =>
  text.split(/\*([^*]+)\*/g).map((seg, i) => (i % 2 === 1 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>));

const now = () => new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

export const WhatsAppSim = () => {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const bottomRef = useRef(null);
  const initRef = useRef(false);

  const sendToBot = async (text, sid = sessionId, showUser = true) => {
    if (showUser) setMessages((m) => [...m, { from: "user", text, time: now() }]);
    setOptions([]);
    setTyping(true);
    try {
      const { data } = await api.post("/whatsapp/bot", {
        session_id: sid,
        message: text,
        origin_url: window.location.origin,
      });
      for (const reply of data.replies) {
        await new Promise((r) => setTimeout(r, 450));
        setMessages((m) => [...m, { from: "bot", text: reply, time: now() }]);
      }
      setOptions(data.options || []);
    } catch (e) {
      setMessages((m) => [...m, { from: "bot", text: "⚠️ Error de conexión con el bot. Inténtalo de nuevo.", time: now() }]);
    } finally {
      setTyping(false);
    }
  };

  const restart = () => {
    const sid = crypto.randomUUID();
    setSessionId(sid);
    setMessages([]);
    sendToBot("hola", sid, false);
  };

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    sendToBot("hola", sessionId, false);
    api.get("/whatsapp/status").then(({ data }) => setWaStatus(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const submit = (e) => {
    e.preventDefault();
    const t = input.trim();
    if (!t || typing) return;
    setInput("");
    sendToBot(t);
  };

  return (
    <div data-testid="whatsapp-sim" className="mx-auto max-w-2xl">
      <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <p className="font-sans text-sm text-white/70">
          <strong className="text-cyber-cyan">Simulación del bot de WhatsApp.</strong> Así responderá el número{" "}
          <span className="font-mono-tech">+34 607 20 21 06</span> cuando se conecte a la API de WhatsApp Business.
          Prueba la conversación completa: menú, precios, contratación y pago.
        </p>
      </div>

      {waStatus && (
        <div
          data-testid="wa-connection-status"
          className={`mb-4 rounded-xl border p-4 ${
            waStatus.configured ? "border-emerald-400/30 bg-emerald-400/[0.06]" : "border-amber-400/30 bg-amber-400/[0.06]"
          }`}
        >
          {waStatus.configured ? (
            <p className="font-sans text-sm text-emerald-300">✅ WhatsApp real conectado: el bot responde automáticamente a los mensajes entrantes.</p>
          ) : (
            <div className="font-sans text-sm text-white/70">
              <p className="font-semibold text-amber-300">⚠️ WhatsApp real todavía no conectado (el motor del bot ya está listo)</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-white/60">
                <li>Crea una cuenta en <span className="text-white/85">developers.facebook.com</span> → Mis Apps → Crear app → "Conectar con clientes por WhatsApp".</li>
                <li>En WhatsApp → Configuración de API: conecta tu número +34 607 20 21 06 y copia el <strong>Access Token permanente</strong> y el <strong>Phone Number ID</strong>, y pégamelos aquí en el chat.</li>
                <li>
                  En Webhooks configura — URL:{" "}
                  <code className="break-all rounded bg-black/40 px-1.5 py-0.5 font-mono-tech text-[11px] text-cyber-cyan">
                    {process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook
                  </code>
                </li>
                <li>
                  Token de verificación:{" "}
                  <code className="break-all rounded bg-black/40 px-1.5 py-0.5 font-mono-tech text-[11px] text-cyber-cyan">{waStatus.verify_token}</code>{" "}
                  y suscríbete al campo <strong>messages</strong>.
                </li>
              </ol>
            </div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
        {/* Header WhatsApp */}
        <div className="flex items-center justify-between bg-[#1f2c34] px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0b141a]">
              <img src="/cyvr-logo.png" alt="CYVR" className="h-7 w-7 object-contain" />
            </div>
            <div>
              <p className="font-sans text-sm font-semibold text-white">CYVR Labs</p>
              <p className="font-sans text-[11px] text-[#8696a0]">{typing ? "escribiendo…" : "Bot automático · en línea"}</p>
            </div>
          </div>
          <button
            onClick={restart}
            data-testid="wa-restart-btn"
            className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-white/70 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan"
          >
            <RotateCcw className="h-3 w-3" /> Reiniciar
          </button>
        </div>

        {/* Chat */}
        <div
          data-testid="wa-chat-area"
          className="h-[480px] overflow-y-auto bg-[#0b141a] px-4 py-4"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "18px 18px" }}
        >
          {messages.map((m, i) => (
            <div key={i} className={`mb-2 flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={m.from === "user" ? "wa-msg-user" : "wa-msg-bot"}
                className={`max-w-[80%] rounded-lg px-3 py-2 text-[13.5px] leading-relaxed text-[#e9edef] shadow ${
                  m.from === "user" ? "rounded-tr-none bg-[#005c4b]" : "rounded-tl-none bg-[#202c33]"
                }`}
              >
                <p className="whitespace-pre-line font-sans">{linkify(m.text)}</p>
                <p className="mt-1 flex items-center justify-end gap-1 text-right text-[10px] text-[#8696a0]">
                  {m.time} {m.from === "user" && <CheckCheck className="h-3 w-3 text-[#53bdeb]" />}
                </p>
              </div>
            </div>
          ))}
          {typing && (
            <div className="mb-2 flex justify-start">
              <div className="rounded-lg rounded-tl-none bg-[#202c33] px-4 py-3">
                <span className="inline-flex gap-1">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8696a0]" style={{ animationDelay: `${d * 0.15}s` }} />
                  ))}
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick replies */}
        {options.length > 0 && (
          <div data-testid="wa-quick-replies" className="flex flex-wrap gap-2 bg-[#0b141a] px-4 pb-3">
            {options.map((o) => (
              <button
                key={o}
                onClick={() => sendToBot(o)}
                data-testid="wa-quick-reply-btn"
                className="rounded-full border border-[#00a884]/50 bg-[#00a884]/10 px-4 py-1.5 font-sans text-xs text-[#00a884] transition-colors hover:bg-[#00a884]/25"
              >
                {o}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <form onSubmit={submit} className="flex items-center gap-2 bg-[#1f2c34] px-3 py-2.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe un mensaje…"
            data-testid="wa-input"
            className="flex-1 rounded-full bg-[#2a3942] px-4 py-2.5 font-sans text-sm text-[#e9edef] placeholder-[#8696a0] outline-none"
          />
          <button
            type="submit"
            disabled={typing || !input.trim()}
            data-testid="wa-send-btn"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00a884] text-white transition-opacity disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
};
